import useParkingService from '../../../hooks/parking-services/useParkingService.ts';
import useUpdateParkingServiceMetadata from '../../../hooks/parking-services/useUpdateParkingServiceMetadata.ts';
import MutateParkingServiceParkingInfoForm, {
  MutateParkingServiceParkingInfoFormValues,
} from '../../ui/places/MutateParkingServiceParkingInfoForm.tsx';
import {
  UpdateParkingServiceMetadataRequest,
  UpdateParkingServiceParkingSpacesRequest,
} from '../../../api/requests/parking-service-request.ts';
import { notifyError } from '../../../utils/NotifyError.tsx';
import { LoadingOverlay, Modal, Stepper } from '@mantine/core';
import { useState } from 'react';
import MutateParkingServiceParkingSpacesForm, {
  MutateParkingServiceParkingSpacesFormValues,
} from '../../ui/places/MutateParkingServiceParkingSpacesForm.tsx';
import useUpdateParkingServiceParkingSpaces from '../../../hooks/parking-services/useUpdateParkingServiceParkingSpaces.ts';
import { useMutation } from '@tanstack/react-query';
import { ErrorResponse } from '../../../api/responses/error-response.ts';

interface UpdateParkingServiceParkingInfoModalProps {
  opened: boolean;
  onClose: () => void;
  serviceId: string;
  onSuccess?: () => void;
}

const UpdateParkingServiceParkingInfoModal = ({
  opened,
  onClose,
  serviceId,
  onSuccess,
}: UpdateParkingServiceParkingInfoModalProps) => {
  const [active, setActive] = useState(0);

  const { data: parkingService, isLoading: isParkingServiceLoading } =
    useParkingService(serviceId);

  const { mutateAsync: updateParkingServiceMetadata } =
    useUpdateParkingServiceMetadata();
  const { mutateAsync: updateParkingServiceParkingSpaces } =
    useUpdateParkingServiceParkingSpaces();

  const {
    mutate: handleSubmitParkingInfo,
    isPending: isUpdateParkingInfoPending,
  } = useMutation<
    void,
    ErrorResponse,
    MutateParkingServiceParkingInfoFormValues
  >({
    mutationFn: async (formValues) => {
      const updateParkingServiceMetadataRequest: UpdateParkingServiceMetadataRequest =
        {
          brandId: parkingService.brandId ?? '',
          buildingTypes: formValues.buildingTypes,
          floorLevels: formValues.floorLevels,
          hasElevator: formValues.hasElevator
            ? formValues.hasElevator
            : 'UNKNOWN',
          hasParkingBooth: formValues.hasParkingBooth
            ? formValues.hasParkingBooth
            : 'UNKNOWN',
          hasRestroom: formValues.hasRestroom
            ? formValues.hasRestroom
            : 'UNKNOWN',
          heightLimit:
            formValues.heightLimit !== ''
              ? parseFloat(formValues.heightLimit)
              : null,
          mechanicalSpaces: formValues.mechanicalSpaces
            ? formValues.mechanicalSpaces
            : 'UNKNOWN',
          name: parkingService.name,
          notes: parkingService.notes,
          parkingLotTypes: formValues.parkingLotTypes,
          phoneNumber: parkingService.phoneNumber,
          pricingSource: parkingService.pricingSource,
          website: parkingService.website,
          serviceId: serviceId,
        };

      await updateParkingServiceMetadata(updateParkingServiceMetadataRequest);
    },
    onSuccess: () => {
      setActive(1);
    },
    onError: (error) => {
      notifyError('更新停車資訊失敗', error.message);
    },
  });

  const {
    mutate: handleSubmitParkingSpaces,
    isPending: isUpdateParkingSpacesPending,
  } = useMutation<
    void,
    ErrorResponse,
    MutateParkingServiceParkingSpacesFormValues
  >({
    mutationFn: async (formValues) => {
      const updateParkingServiceParkingSpacesRequest: UpdateParkingServiceParkingSpacesRequest =
        {
          availableCarSpaces:
            formValues.availableCarSpaces !== ''
              ? Number(formValues.availableCarSpaces)
              : null,
          availableMotoSpaces:
            formValues.availableMotoSpaces !== ''
              ? Number(formValues.availableMotoSpaces)
              : null,
          serviceId: serviceId,
          totalCarSpaces:
            formValues.totalCarSpaces !== ''
              ? Number(formValues.totalCarSpaces)
              : null,
          totalDisabledParkingSpaces:
            formValues.totalDisabledParkingSpaces !== ''
              ? Number(formValues.totalDisabledParkingSpaces)
              : null,
          totalHeavyMotoSpaces:
            formValues.totalHeavyMotoSpaces !== ''
              ? Number(formValues.totalHeavyMotoSpaces)
              : null,
          totalMaternityParkingSpaces:
            formValues.totalMaternityParkingSpaces !== ''
              ? Number(formValues.totalMaternityParkingSpaces)
              : null,
          totalMotoSpaces:
            formValues.totalMotoSpaces !== ''
              ? Number(formValues.totalMotoSpaces)
              : null,
        };

      await updateParkingServiceParkingSpaces(
        updateParkingServiceParkingSpacesRequest,
      );
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      notifyError('更新停車資訊失敗', error.message);
    },
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="編輯停車資訊"
      centered
      closeOnClickOutside={false}
      size={659}
      styles={{ title: { fontWeight: 700, fontSize: 16 } }}
    >
      {isParkingServiceLoading ? (
        <LoadingOverlay visible />
      ) : (
        <Stepper
          size={'sm'}
          active={active}
          allowNextStepsSelect={false}
          onStepClick={setActive}
          styles={{
            root: {
              justifyItems: 'center',
            },
            steps: {
              width: 302,
              paddingBottom: 24,
            },
            content: {
              width: 627,
              paddingTop: 0,
            },
          }}
        >
          {/* Step 1: 停車基本資訊 */}
          <Stepper.Step label={'基本資訊'}>
            <MutateParkingServiceParkingInfoForm
              initialValues={{
                mechanicalSpaces: parkingService.mechanicalSpaces,
                buildingTypes: parkingService.buildingTypes,
                parkingLotTypes: parkingService.parkingLotTypes,
                hasRestroom: parkingService.hasRestroom,
                hasElevator: parkingService.hasElevator,
                hasParkingBooth: parkingService.hasParkingBooth,
                floorLevels: parkingService.floorLevels,
                heightLimit:
                  parkingService.heightLimit !== null
                    ? parkingService.heightLimit.toString()
                    : '',
              }}
              onSubmit={handleSubmitParkingInfo}
              onCancel={onClose}
              isSubmitting={isUpdateParkingInfoPending}
              submitButtonText={'下一步'}
            />
          </Stepper.Step>

          {/* Step 2: 停車車位資訊 */}
          <Stepper.Step label={'車位資訊'}>
            <MutateParkingServiceParkingSpacesForm
              initialValues={{
                availableCarSpaces:
                  parkingService.parkingSpaceInfo.availableCarSpaces !== null
                    ? parkingService.parkingSpaceInfo.availableCarSpaces.toString()
                    : '',
                totalCarSpaces:
                  parkingService.parkingSpaceInfo.totalCarSpaces !== null
                    ? parkingService.parkingSpaceInfo.totalCarSpaces.toString()
                    : '',
                availableMotoSpaces:
                  parkingService.parkingSpaceInfo.availableMotoSpaces !== null
                    ? parkingService.parkingSpaceInfo.availableMotoSpaces.toString()
                    : '',
                totalMotoSpaces:
                  parkingService.parkingSpaceInfo.totalMotoSpaces !== null
                    ? parkingService.parkingSpaceInfo.totalMotoSpaces.toString()
                    : '',
                totalHeavyMotoSpaces:
                  parkingService.parkingSpaceInfo.totalHeavyMotoSpaces !== null
                    ? parkingService.parkingSpaceInfo.totalHeavyMotoSpaces.toString()
                    : '',
                totalDisabledParkingSpaces:
                  parkingService.parkingSpaceInfo.totalDisabledParkingSpaces !==
                  null
                    ? parkingService.parkingSpaceInfo.totalDisabledParkingSpaces.toString()
                    : '',
                totalMaternityParkingSpaces:
                  parkingService.parkingSpaceInfo
                    .totalMaternityParkingSpaces !== null
                    ? parkingService.parkingSpaceInfo.totalMaternityParkingSpaces.toString()
                    : '',
              }}
              onSubmit={handleSubmitParkingSpaces}
              onCancel={onClose}
              isSubmitting={isUpdateParkingSpacesPending}
              submitButtonText={'更新'}
            />
          </Stepper.Step>
        </Stepper>
      )}
    </Modal>
  );
};

export { UpdateParkingServiceParkingInfoModal };
